import { matchClosedSet, normalize } from '@rally/core';
import type { AgentEvent, ToolClient, ToolInfo, ToolOutcome, TurnResult } from './types.ts';

/**
 * Deterministic offline planner: the simulated Alexa+ brain when no model is
 * configured. A rule-based dialogue policy that drives the very same MCP tools
 * the LLM planner uses, so the demo runs anywhere and CI can exercise every
 * tool end to end. It covers Rally's conversations: starting a plan (asking
 * who to invite when that's missing), "yes" to Rally's suggested next step,
 * status, nudges, places, "the second one", changes ("actually, make it
 * Sunday"), adding someone, messages to the group, "what am I doing Saturday",
 * notes, cancelling, "tell me more", and "what can you do?".
 */

type Frame =
  | { kind: 'start'; args: Record<string, unknown>; kindWord?: string }
  | { kind: 'fill'; field: 'organizer' | 'honoree'; args: Record<string, unknown> }
  | { kind: 'confirm'; tool: string; args: Record<string, unknown> }
  | { kind: 'when'; args: Record<string, unknown> };

/** Rally's own offer, with the consent binding it issued ("Want me to nudge Sam?"). */
type NextStep = { action: string; people?: string[]; confirmation?: string; message?: string };
type Call = (name: string, args: Record<string, unknown>) => Promise<ToolOutcome>;
type SpeechOf = (o: ToolOutcome) => string;

const YES_WORDS = "yes please|yes|yeah|yep|yup|sure thing|sure|ok|okay|alright|all right|go ahead|do it|please do|send them|send it|sounds good|perfect|absolutely|definitely|of course|confirm|book it|lock it in|that works|that's right|correct|go for it|let's do it|please";
const YES = new RegExp(`^(?:${YES_WORDS})\\b`);
/** The whole utterance agrees ("Yes", "Yeah, go ahead", "Sure, send them"). "Yes, but…" is not a yes. */
const PLAIN_YES = new RegExp(`^(?:${YES_WORDS})(?:\\s+(?:and\\s+)?(?:${YES_WORDS}|now|then|right away|thanks|thank you|alexa|rally))*$`);
const NO = /^(no thanks|no thank you|definitely not|absolutely not|of course not|no way|not really|no|nope|nah|not now|not yet|never mind|nevermind|don't|dont|stop|hold on|hang on|hold off|wait|cancel that)\b/;
/** Words people start with that don't change the request: "Actually, make it Sunday", "Oh, and invite Priya too". */
const FILLER = /^(?:(?:actually|wait|oh|so|um+|uh+|er+|hmm+|and|well|hey|no wait|oh wait|sorry|oops|i mean|i meant)[\s,]+)+/;
/**
 * Brevity from explicit words only ("I'm in a hurry", "short version"), never guessed from a voice.
 * Read-backs before anything that commits stay whole: a yes has to be to the full read-back.
 */
const HURRY = /\s*,?\s*\b(?:i'?m in a (?:hurry|rush)|i'?m driving|(?:just )?(?:the )?short version|keep it short|quick(?:ly)?,|briefly|no time|just the (?:headline|basics))\b[\s,.!]*/i;
const MORE = /\b(?:tell me more|more detail|the full (?:version|details)|in detail|what else|go on|keep going)\b/i;
const READ_ONLY = new Set(['plan_status', 'whats_planned', 'find_places', 'recall']);

const HELP =
  /^(?:(?:can you |could you |please )?help(?: me)?(?: out)?(?: please)?|what (?:else )?(?:can|could|do|does) (?:you|i|rally) (?:do|say|ask)(?: (?:here|for me|with (?:rally|this|you)))?|what (?:else )?can you help (?:me )?with|how (?:does|do) (?:this|it|rally|you) work|what are (?:my|the|your) options|what(?:'s| is) rally|who are you|what should i (?:say|do)|i'?m (?:lost|confused|stuck)|what now|(?:hi|hello|hey|good (?:morning|afternoon|evening))(?: (?:alexa|rally|there))?)$/;
const THANKS = /^(?:thanks|thank you|thx|cheers)(?: (?:so much|very much|alexa|rally))*$/;
const REPEAT = /^(?:say (?:that|it) again|repeat (?:that|it)|repeat|come again|what did you say|pardon|what was that)$/;

const DAY = '(?:sun|mon|tues|wednes|thurs|fri|satur)day';
const DATE_PHRASE = new RegExp(
  `(the ${DAY} after next|next (?:week(?:end)?|${DAY})|this (?:week(?:end)?|${DAY})|(?:in two weeks|the week after next)|tomorrow|tonight|(?:on )?${DAY}|(?:jan(?:uary)?|feb(?:ruary)?|march|april|may|june|july|aug(?:ust)?|sept?(?:ember)?|oct(?:ober)?|nov(?:ember)?|dec(?:ember)?) \\d{1,2}(?:st|nd|rd|th)?|the \\d{1,2}(?:st|nd|rd|th))(?:,? at \\d{1,2}(?:[: ]\\d{2})?\\s*(?:am|pm|a m|p m)?)?`,
);
const NUMBER_WORDS: Record<string, number> = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12 };
const ORDINALS: Record<string, string> = { first: 'first', '1st': 'first', one: 'first', '1': 'first', second: 'second', '2nd': 'second', two: 'second', '2': 'second', third: 'third', '3rd': 'third', three: 'third', '3': 'third' };
/** Tastes the places can be filtered by (cuisine, features, neighborhood). */
const PREFERENCE = /\b(mexican|italian|thai|bangladeshi|japanese|vegan|seafood|quiet|bowling|rooftop|cocktails|halal|patio|lively|cozy|views|fremont|ballard|capitol hill|waterfront)\b/;
const PICK = /^(?:(?:let's do|lets do|let's go with|i'll take|i'd like|how about|make it|go with) )?(?:the |number |option |choice |no )?(first|second|third|1st|2nd|3rd|one|two|three|1|2|3)(?: one| option| place| choice| please)*$/;

const ORDINAL_WORDS: Record<string, string> = {
  twentieth: '20th', thirtieth: '30th', fortieth: '40th', fiftieth: '50th', sixtieth: '60th', seventieth: '70th', eightieth: '80th', ninetieth: '90th', hundredth: '100th', eighteenth: '18th', twenty_first: '21st',
};
/** Words for a group of people, as opposed to a person: "the family", "the book club", "his golf buddies". */
const GROUP = /\b(family|club|buddies|friends|crew|gang|team|group|neighbou?rs|cousins|kids|grandkids|parents|in-laws|colleagues|coworkers|co-workers|girls|guys|folks|squad|class|league|choir|circle|golf)\b/;
const RELATION = '(?:husband|wife|dad|mom|mum|mother|father|son|daughter|partner|boyfriend|girlfriend|brother|sister|grandma|grandpa|grandmother|grandfather|boss|best friend)';
/** What follows "for" or "with" that is never a guest: "for dinner", "for two", "for us". */
const NOT_GUESTS = /^(?:a|an|some|the (?:weekend|week|evening|night|day)|us|me|you|myself|everyone|everybody|all of us|two|three|four|five|six|seven|eight|nine|ten|\d+|dinner|drinks|brunch|lunch|breakfast|coffee|a drink|tonight|tomorrow|later|now|it|that|this|real)\b/;
const KINDS: Array<[RegExp, 'dinner' | 'party' | 'drinks' | 'brunch' | 'activity', string?]> = [
  [/\bdinner\b/, 'dinner'],
  [/\b(?:party|birthday|celebrat\w*|anniversary)\b/, 'party'],
  [/\bdrinks?\b/, 'drinks'],
  [/\bbrunch\b/, 'brunch'],
  [/\blunch\b/, 'brunch', 'lunch'],
  [/\b(bowling|hike|movie night|movie|game night|barbecue|bbq|picnic|outing)\b/, 'activity'],
];

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
/** Days and months as they're written: "the saturday after next" → "the Saturday after next". */
const spokenWhen = (w: string) => w.replace(/\b(sun|mon|tues|wednes|thurs|fri|satur)day\b|\b(jan|feb|march|april|may|june|july|aug|sept?|oct|nov|dec)\w*/g, (m) => cap(m)).replace(/^./, (c) => c.toUpperCase());

export class OfflineVoiceAgent {
  readonly planner = 'offline:rules';
  readonly #tools: ToolClient;
  #toolInfo?: Map<string, ToolInfo>;
  #brief = false;
  #frame?: Frame;
  #planId?: string;
  #next?: NextStep;
  /** The last read-only question asked, so "tell me more" asks it again in full. */
  #lastRead?: { name: string; args: Record<string, unknown> };
  #lastSpeech?: string;
  /** The names find_places just read out, so a misheard "O'Cross's Cantina" still means Rosa's Cantina. */
  #places: string[] = [];

  constructor(opts: { tools: ToolClient }) {
    this.#tools = opts.tools;
  }

  async #info(): Promise<Map<string, ToolInfo>> {
    this.#toolInfo ??= new Map((await this.#tools.listTools()).map((t) => [t.name, t]));
    return this.#toolInfo;
  }

  async turn(utterance: string, onEvent: (e: AgentEvent) => void = () => {}): Promise<TurnResult> {
    const started = performance.now();
    const calls: TurnResult['toolCalls'] = [];
    const info = await this.#info();

    const call = async (name: string, args: Record<string, unknown>): Promise<ToolOutcome> => {
      const id = `call_${calls.length + 1}`;
      onEvent({ type: 'tool_call', id, name, args });
      const outcome = await this.#tools.callTool(name, args);
      onEvent({ type: 'tool_result', id, name, outcome });
      calls.push({ name, args, outcome });
      const ui = outcome.uiResourceUri ?? info.get(name)?.uiResourceUri;
      if (ui && !outcome.isError && outcome.data) onEvent({ type: 'ui', resourceUri: ui, toolName: name, data: outcome.data });
      this.#learn(name, args, outcome);
      return outcome;
    };
    const speechOf = (o: ToolOutcome) => (typeof o.data?.speech === 'string' ? o.data.speech : o.text) || 'Done.';

    if (MORE.test(utterance)) this.#brief = false;
    const hurried = HURRY.test(utterance);
    if (hurried) this.#brief = true;
    const asked = hurried ? utterance.replace(HURRY, ' ').trim() : utterance;
    let speech: string;
    try {
      speech = hurried && !/[a-z]{3}/i.test(asked) ? 'Okay, short answers.' : await this.#respond(asked, call, speechOf);
    } catch (err) {
      speech = `Sorry, something went wrong: ${(err as Error).message}`;
    }
    // Short answers: the first sentence and any question, for replies that don't ask for a commitment.
    const last = calls.at(-1);
    if (this.#brief && last && READ_ONLY.has(last.name) && !last.outcome.data?.secret) {
      const sentences = speech.match(/[^.!?]+[.!?]+/g)?.map((x) => x.trim()) ?? [speech];
      const question = sentences.length > 1 && sentences.at(-1)!.endsWith('?') ? sentences.at(-1) : undefined;
      // "Want to hear them?" keeps the sentence that says what they are.
      const antecedent = question && sentences.length > 2 && /\b(them|those|these)\b/.test(question) ? sentences.at(-2) : undefined;
      speech = [sentences[0], antecedent, question].filter(Boolean).join(' ');
    }
    this.#lastSpeech = speech;
    onEvent({ type: 'speech', text: speech });
    const latencyMs = Math.round(performance.now() - started);
    onEvent({ type: 'done', latencyMs, steps: calls.length });
    return { speech, toolCalls: calls, latencyMs, planner: this.planner };
  }

  /** Remember what Rally said so "yes", "the second one" and "tell me more" mean the right thing next turn. */
  #learn(name: string, args: Record<string, unknown>, outcome: ToolOutcome) {
    const d = outcome.data ?? {};
    if (typeof d.plan_id === 'string') this.#planId = d.plan_id;
    if (READ_ONLY.has(name) && !outcome.isError) this.#lastRead = { name, args };
    if (d.needs_confirmation === true) {
      // The read-back's confirmation value binds the coming yes to exactly this action.
      this.#frame = { kind: 'confirm', tool: name, args: { ...args, ...(typeof d.confirmation === 'string' ? { confirmation: d.confirmation } : {}) } };
      return;
    }
    // Rally asked who is planning the surprise (it can't tell voices apart), or who it's for.
    if (name === 'start_plan' && (d.code === 'need_organizer' || d.code === 'need_honoree')) {
      this.#frame = { kind: 'fill', field: d.code === 'need_organizer' ? 'organizer' : 'honoree', args };
      return;
    }
    // "I don't have a usual for the book club yet. When should it be?"
    if (name === 'start_plan' && d.code === 'no_routine') {
      const { like_last_time: _usual, ...rest } = args;
      this.#frame = { kind: 'when', args: rest };
      return;
    }
    const card = d.card as { next?: { action?: string; label?: string } } | undefined;
    const action = (typeof d.next_step === 'string' ? d.next_step : undefined) ?? card?.next?.action;
    const confirmation = typeof d.next_confirmation === 'string' ? d.next_confirmation : undefined;
    if (name === 'find_places') {
      this.#next = { action: 'book' };
      this.#places = Array.isArray(d.places) ? (d.places as Array<{ name?: unknown }>).map((p) => String(p.name ?? '')).filter(Boolean) : [];
    }
    else if (action) {
      const label = card?.next?.label ?? '';
      const listed = action === 'nudge' ? label.replace(/^Nudge\s+/i, '').split(/,\s*|\s+and\s+/).filter(Boolean) : undefined;
      // "Nudge Sam, Tom, Maya and 2 more" names only some of them: nudge everyone due rather than pass "2 more" as a name.
      const people = listed?.some((p) => /^\d+ more$/.test(p)) ? undefined : listed;
      this.#next = { action, people, confirmation, message: typeof d.proposed_message === 'string' ? d.proposed_message : undefined };
    } else this.#next = undefined;
  }

  async #respond(raw: string, call: Call, speechOf: SpeechOf): Promise<string> {
    const unfill = (t: string) => t.replace(FILLER, '') || t;
    const strip = (t: string) =>
      unfill(t.replace(/^(hey |ok |okay )?alexa,?\s*/, ''))
        .replace(/^(ask|tell|have) rally (to )?/, '')
        .replace(/^rally,?\s+(?=(plan|how|what|book|find|tell|move|cancel|remember|nudge|forget|make|add|invite))/, '')
        // Speech recognition sometimes runs a command into the next word: "planbook club", "booklark".
        .replace(/^(plan|book|rally|nudge|remind)(?!(?:s|ed|ing|ned|ning|ner|ners|ly|y)\b)(?=[a-z]{3,})/, '$1 ')
        // ...or mishears it: "Playing a surprise dinner…", "Rather the family for dinner…".
        .replace(/^(?:playing|plant|planned) (?=(?:(?:a|an) )?(?:surprise|party|dinner|birthday|brunch|lunch|get together)\b)/, 'plan ')
        .replace(/^(?:rather|raleigh|rally's)\s+(?=(?:the|my|our|his|her) )/, 'rally ')
        // "House Tom's party going?" is "How's Tom's party going?"
        .replace(/^house (?=.+\b(?:going|looking|coming along|shaping up)\??$)/, "how's ")
        .trim();
    // Typed on a phone, apostrophes come curly: "Who’s coming?"
    const said = raw.replace(/[’‘]/g, "'").replace(/[“”]/g, '"');
    // `text` drives intent matching; `listText` keeps commas so "the family, his golf buddies" stays two names.
    let text = strip(normalize(said));
    const listText = strip(said.toLowerCase().replace(/[.!?]+\s*$/, '').replace(/\s+/g, ' ').trim());
    const plan = this.#planId;
    const withPlan = (args: Record<string, unknown>) => (plan && args.plan === undefined ? { ...args, plan } : args);
    const afterLead = (t: string, lead: RegExp) => t.replace(lead, '').replace(/^[\s,]+/, '').replace(/^(?:but|and|though|then|just)\s+/, '').trim();

    // Confirmation of something Rally read back. Only a plain yes confirms; "yes, but…" does not.
    if (this.#frame?.kind === 'confirm') {
      const frame = this.#frame;
      this.#frame = undefined;
      if (PLAIN_YES.test(text)) return speechOf(await call(frame.tool, { ...frame.args, confirm: true }));
      const no = NO.test(text);
      if (no || YES.test(text)) {
        // "No, make it Sunday" or "Yes, but make it Sunday": nothing is confirmed, and the rest is a new request.
        const rest = afterLead(text, no ? NO : YES);
        if (rest && this.#intentOf(rest)) text = rest;
        else return no ? "Okay, I won't." : "Okay, I haven't done anything yet. What should change?";
      }
    }

    // "Tell me more": ask the last question again, in full (the server still decides who may hear it).
    if (MORE.test(text) && !this.#intentOf(text)) return this.#more(text, call, speechOf);

    // Answering "who's planning it?" ("It's David") or "who's the surprise for?" ("For David").
    if (this.#frame?.kind === 'fill' && !this.#intentOf(text)) {
      const frame = this.#frame;
      if (NO.test(text)) {
        this.#frame = undefined;
        return "Okay, I haven't started anything.";
      }
      const name = text.replace(/^(?:(?:it's|it is|i'm|i am|me|this is|for|it's for)[\s,]+)+/, '').replace(/[\s,]+(?:is|here)$/, '').trim();
      if (!name || PLAIN_YES.test(text)) return frame.field === 'organizer' ? "Who's planning it?" : "Who's the surprise for?";
      this.#frame = undefined;
      return speechOf(await call('start_plan', { ...frame.args, [frame.field]: name }));
    }

    // Answering "when should it be?" after Rally found no usual to repeat.
    if (this.#frame?.kind === 'when') {
      const frame = this.#frame;
      const intent = this.#intentOf(text);
      const date = text.match(DATE_PHRASE)?.[0];
      const anyTime = /^(?:whenever|any ?time|you (?:pick|choose|decide)|it doesn't matter|doesn't matter|no preference)\b/.test(text);
      if ((date && (!intent || intent === 'change' || intent === 'whats')) || anyTime) {
        this.#frame = undefined;
        const invite = Array.isArray(frame.args.invite) ? (frame.args.invite as string[]) : [];
        const title = groupTitle(invite);
        return speechOf(await call('start_plan', { ...frame.args, ...(title ? { title } : {}), ...(date ? { when: cleanWhen(date) } : {}) }));
      }
      if (NO.test(text)) {
        this.#frame = undefined;
        return "Okay, I haven't started anything.";
      }
      if (!intent) this.#frame = undefined;
    }

    // Answering "who should I invite?"
    if (this.#frame?.kind === 'start') {
      const frame = this.#frame;
      // "Invite the family" answers the question; it doesn't start another plan.
      const intent = /^(?:please )?(?:just )?invite\b/.test(text) ? undefined : this.#intentOf(text);
      // "Actually, make it Sunday" while Rally is asking who: that's the draft's date.
      const moved = intent === 'change' ? this.#changeOf(text) : undefined;
      if (moved?.when) {
        frame.args = { ...frame.args, when: moved.when };
        return `Okay, ${spokenWhen(moved.when)}. Who should I invite?`;
      }
      if (!intent) {
        // A question isn't a guest list, so it drops the draft instead.
        if (/\?\s*$|^(why|what|how|who|when|where|is|are|do|does|can|could|will|would)\b/.test(said.trim().toLowerCase())) {
          this.#frame = undefined;
          return "Okay, I haven't started anything. What would you like to do?";
        }
        // "Yes", "hmm" or "I don't know" isn't anyone's name.
        if (PLAIN_YES.test(text) || /^(?:hmm+|um+|uh+|er+|huh|what|sorry|pardon|i don't know|i'm not sure|not sure|dunno|let me think)$/.test(text)) return 'Who should I invite? You can say circles, like the family, or names.';
        const no = NO.test(text);
        const answer = no ? afterLead(listText, /^(?:no thanks|no|nope|nah)\b/) : listText;
        this.#frame = undefined;
        if (no && !answer) return "Okay, I haven't started anything.";
        const invite = splitNames(answer.replace(/^(?:(?:please |just |only |oh |um |let's |lets )*(?:invite |how about |do |for |with |the whole )?)/, ''));
        if (!invite.length) return 'Who should I invite? You can say circles, like the family, or names.';
        const args = { ...frame.args };
        if (!args.title && !args.honoree) {
          const title = groupTitle(invite, frame.kindWord);
          if (title) args.title = title;
        }
        return speechOf(await call('start_plan', { ...args, invite }));
      }
    }

    // "Yes" to Rally's suggested next step; "no" sets it aside.
    if (NO.test(text)) {
      const rest = afterLead(text, NO);
      this.#next = undefined;
      if (!rest || !this.#intentOf(rest)) return 'Okay. Just ask me when you want to pick it back up.';
      text = rest;
    } else if (YES.test(text)) {
      const rest = afterLead(text, YES);
      if (PLAIN_YES.test(text)) {
        const next = this.#next;
        // start_plan already read the guest list and date back and asked "Shall I send the invites?"; this yes answers it.
        const bound = next?.confirmation ? { confirmation: next.confirmation } : {};
        if (next?.action === 'send_invites') return speechOf(await call('send_invites', withPlan({ confirm: true, ...bound })));
        // Rally asked "Want me to nudge Sam?", so this yes is the consent.
        if (next?.action === 'nudge') return speechOf(await call('nudge_guests', withPlan({ ...(next.people?.length ? { people: next.people } : {}), confirm: true, ...bound })));
        if (next?.action === 'choose_venue') return speechOf(await call('find_places', withPlan({})));
        // "My pick is Lark & Lantern… Book it?" Yes books the pick (book_place still reads it back first).
        if (next?.action === 'book') return speechOf(await call('book_place', withPlan({ place: 'the first one' })));
        if (next?.action === 'day_of') return speechOf(await call('plan_status', withPlan({})));
        // After the reveal: "Want to send them a thank-you?"
        if (next?.action === 'thank_guests') return speechOf(await call('message_guests', withPlan({ message: next.message ?? 'Thank you for my surprise. It meant the world to me!', confirm: true, ...bound })));
        return 'Okay. What would you like to do?';
      }
      // "Yes, tell everyone…" is a request of its own; "yes, but not Sam" is not a yes.
      if (rest && this.#intentOf(rest)) text = rest;
      else return this.#next ? "Okay, I'll hold off. What would you like to change?" : 'Okay. What would you like to do?';
    }

    const intent = this.#intentOf(text);
    switch (intent) {
      case 'help':
        return this.#help();
      case 'thanks':
        return "You're welcome.";
      case 'remove':
        return "Sorry, I can't take someone off a plan yet.";
      case 'reminder':
        return "I can't set reminders for you, but I can nudge guests who haven't answered.";
      case 'repeat':
        return this.#lastSpeech ?? this.#fallback(text);
      case 'invite':
        return speechOf(await call('send_invites', withPlan({})));
      case 'add': {
        const adding = this.#addOf(listText) ?? this.#addOf(text);
        const invite = splitNames(adding?.who ?? '');
        if (!invite.length) return 'Who should I add?';
        const schema = (await this.#info()).get('send_invites')?.inputSchema as { properties?: Record<string, unknown> } | undefined;
        if (!schema?.properties?.add) return "Sorry, I can't add people to a plan yet.";
        return speechOf(await call('send_invites', adding?.plan ? { plan: adding.plan, add: invite } : withPlan({ add: invite })));
      }
      case 'message': {
        const m = text.match(/^(?:tell|let|message|text|remind) (?:everyone|everybody|the group|all the guests|the guests|them|the gang)(?: know)?(?: that)?\s*(.*)$/);
        const message = cap((m?.[1] ?? '').replace(/^i'?m\b/, "I'm").replace(/^i\b/, 'I')).trim();
        if (!message) return 'What should I tell everyone?';
        const original = listText.match(/^(?:yes,?\s*)?(?:tell|let|message|text|remind) (?:everyone|everybody|the group|all the guests|the guests|them|the gang)(?: know)?(?: that)?\s*(.*)$/)?.[1];
        const words = cap((original ?? message).replace(/^to\s+/, '').replace(/^i'?m\b/, "I'm").replace(/^i\b/, 'I')).trim();
        return speechOf(await call('message_guests', withPlan({ message: /[.!?]$/.test(words) ? words : `${words}.` })));
      }
      case 'remember': {
        const note = subjectAndRest(text.replace(/^remember (that )?/, ''));
        if (!note) return 'What should I remember, and about whom?';
        return speechOf(await call('remember', { about: note.about, note: note.rest }));
      }
      case 'cancel':
        return speechOf(await call('cancel_plan', withPlan({})));
      case 'recall': {
        const about = text.replace(/^what (?:do|did) you (?:know|remember|have) (?:about|on) |^what(?:'s| is) (?:our|the|my) usual (?:for )?|^(?:do you )?remember anything about /, '').replace(/^(?:the|our|my) (?=\w+ \w+)/, '').trim();
        return speechOf(await call('recall', { about: about || 'me' }));
      }
      case 'forget':
        return this.#forget(text, call, speechOf);
      case 'change': {
        const change = this.#changeOf(text)!;
        if (change.ask) return change.ask;
        return speechOf(await call('change_plan', withPlan({ ...(change.plan ? { plan: change.plan } : {}), ...(change.when ? { when: change.when } : { place: change.place }) })));
      }
      case 'pick': {
        // "The second one", "number two", "option 3", right after the places were read out.
        const n = text.match(PICK)?.[1] ?? '';
        return speechOf(await call('book_place', withPlan({ place: `the ${ORDINALS[n] ?? 'first'} one` })));
      }
      case 'book': {
        const heard = this.#placeNamed(text);
        if (heard) return speechOf(await call('book_place', withPlan({ place: heard })));
        const place =
          text
            .replace(/^book(?=[a-z]{3,})(?!s\b|ed\b|ing\b)/, 'book ')
            .replace(/^.*?\b(?:book|go with|let's go with|lets go with|pick|choose)\b\s*/, '')
            // "Book a table for six at Rosa's" names Rosa's.
            .replace(/^(?:a |the )?(?:table|room|private room|spot)(?: for \w+)?(?: at| in)?\s+/, '') || 'the first one';
        return speechOf(await call('book_place', withPlan({ place })));
      }
      case 'places': {
        const pref = text.match(PREFERENCE)?.[1];
        return speechOf(await call('find_places', withPlan(pref ? { preference: pref } : {})));
      }
      case 'nudge': {
        const who = text.replace(/^.*?\b(?:nudge|remind(?:er)?|ping|chase)\b\s*(?:to\s+)?/, '').replace(/\b(again|please|up)\b/g, '').trim();
        const people = who && !/^(everyone|everybody|them|the rest|people)\b/.test(who) ? splitNames(who) : undefined;
        return speechOf(await call('nudge_guests', withPlan(people?.length ? { people } : {})));
      }
      case 'whats': {
        const when = text.match(DATE_PHRASE)?.[0];
        return speechOf(await call('whats_planned', when ? { when: cleanWhen(when) } : {}));
      }
      case 'status': {
        const ref =
          text.match(/\bhow(?:'s| is| are)\s+(?:the |my |our )?(.+?)\s+(?:going|coming along|coming|looking|shaping up)\b/)?.[1] ??
          text.match(/\b(?:coming|going) to (?:the |my |our )?(.+)$/)?.[1] ??
          text.match(/\bwhat(?:'s| is) (?:going on|happening|new) with (?:the |my |our )?(.+)$/)?.[1] ??
          text.match(/^when(?:'s| is) (?:the |my |our )?(.+)$/)?.[1];
        // "So what was the secret about Saturday?" is about a surprise, not the plan last discussed: the words let Rally find it.
        const secret = text.match(/\b(?:the )?(?:secret|surprise)\b.*$/)?.[0];
        const args = ref && !/^(it|things|everything|that|this|the plan|plan)$/.test(ref) ? { plan: ref } : secret ? { plan: secret } : withPlan({});
        return speechOf(await call('plan_status', args));
      }
      case 'start':
        return this.#start(text, listText, call, speechOf);
      default:
        return this.#fallback(text);
    }
  }

  /** Say so rather than guess: a misheard request gets an honest "didn't catch that" and one thing to try. */
  #fallback(text: string): string {
    if (!/[a-z]{2}/.test(text)) return "Sorry, I didn't catch that.";
    if (this.#next?.action === 'send_invites') return "Sorry, I didn't catch that. Want me to send the invites?";
    return "Sorry, I didn't catch that. Try: what's planned, or rally the book club for dinner.";
  }

  /** "What can you do?": what Rally is for, one thing to try, and a question, fitted to where things stand. */
  #help(): string {
    if (this.#next?.action === 'send_invites') return "I get your people together: a date, the invites, and a place. Your plan is drafted and nothing's gone out yet. Want me to send the invites?";
    if (this.#planId) return "I get your people together: a date, the invites, reminders, and a place. You can ask how it's going. What would you like to do?";
    return 'I get your people together: a date that works, the invites, and a place. Try: rally the book club for dinner next week. Want to start one?';
  }

  async #more(text: string, call: Call, speechOf: SpeechOf): Promise<string> {
    const about = text.match(/\babout (?!(?:it|that|this|them)$)(?:the |my |our )?(.+)$/)?.[1];
    if (about) return speechOf(await call('plan_status', { plan: about }));
    const last = this.#lastRead;
    if (last) return speechOf(await call(last.name, last.args));
    return this.#lastSpeech ?? "That's all I have for now. Want to hear what's planned?";
  }

  async #forget(text: string, call: Call, speechOf: SpeechOf): Promise<string> {
    const body = text.replace(/^forget (?:that |about |what you know about |everything (?:you know )?about |all about )?/, '').replace(/^forget$/, '').trim();
    // "Forget it" is "never mind".
    if (!body || /^(?:it|that|this|about it|it then|that then)$/.test(body)) {
      this.#frame = undefined;
      return 'Okay, never mind.';
    }
    // A circle's routine: "the book club usual", "the book club's usual", "our usual for book club".
    const usual =
      body.match(/^(?:the |our |my )?(.+?)(?:'s)? (?:usual|routine|habit|regular)(?: (?:plan|spot|place|time|day))?$/)?.[1] ??
      body.match(/^(?:the |our |my )?(?:usual|routine|habit)(?: (?:plan|spot|place|time|day))? (?:for|of|with) (?:the |our |my )?(.+)$/)?.[1];
    if (usual) return speechOf(await call('forget', { about: usual, what: 'usual' }));
    // "Forget that Tom can't do Mondays"; with no verb, all of it names who: "forget the golf buddies".
    const note = subjectAndRest(body, false);
    if (note) return speechOf(await call('forget', { about: note.about, what: note.rest }));
    const about = body.replace(/^(?:the|our|my)\b\s*/, '').trim();
    if (!about) return 'What should I forget?';
    return speechOf(await call('forget', { about }));
  }

  /** "Move it to Sunday", "make it 8", "can we do Saturday instead?", "change the place to Rosa's". */
  #changeOf(text: string): { when?: string; place?: string; plan?: string; ask?: string } | undefined {
    // "Push it back an hour": a shift with no new time said, so ask for one.
    if (/\b(?:push|move|bump|shift)(?: it| everything| things| the (?:time|plan|dinner|party|date))? (?:back|forward|up|later|earlier)\b/.test(text) && !/\b(?:to|till|until)\b/.test(text)) {
      return { ask: /\b(?:day|days|week|weekend)\b/.test(text) ? 'Sure. What day should it be?' : 'Sure. What time should it be?' };
    }
    const moved = text.match(/\b(?:move|change|switch|push|reschedule|bump|shift)\b\s*(.*?)\s*\b(?:to|for|till|until)\b\s*(.*)$/);
    const made = text.match(/^(?:can we |could we |can you |could you |let's |lets |please )?make it\s+(.+)$/)?.[1];
    const tried = text.match(/^(?:can we |could we |let's |lets |how about |what about |or |maybe )(?:do |have it |make it |move it to |go with )?(?:it )?(.+?)(?: instead)?$/)?.[1];
    const target = (moved?.[2] ?? made ?? tried ?? '').replace(/\s+(?:for everyone|for everybody|for all|instead|please|then|okay)$/, '').replace(/^(?:on|to) /, '').trim();
    if (!target) return undefined;
    const date = target.match(DATE_PHRASE)?.[0];
    const time = timeOf(target);
    // "Can we do Saturday instead?" is a move only when all that's left is the day, the time, or "at" a place.
    if (!moved && !made && !((date && target.replace(DATE_PHRASE, '').trim() === '') || time || /^at \w/.test(target))) return undefined;
    const ref = moved?.[1]?.replace(/\s*\b(?:back|forward|up|over|out|along)$/, '').replace(/^(?:the|my|our) /, '');
    const plan = ref && !/^(?:it|that|this|everything|things|them|plan|date|day|time|place|venue|restaurant|reservation|booking|dinner|party|drinks|brunch|thing|whole thing|everyone)$/.test(ref) ? ref : undefined;
    if (date) return { when: cleanWhen(date), plan };
    if (time) return { when: time, plan };
    if (/^(?:a bit |an hour |half an hour |a little )?(?:earlier|later)$/.test(target)) return { ask: 'Sure. What time should it be?' };
    // "Can we do it at Rosa's instead?"
    if (tried && !moved && !made && /^at /.test(target)) return { place: target.slice(3) };
    // "Change the date for David's party": no new date was said, so ask for one.
    if (moved && /\b(?:date|day|time|when)\b/.test(moved[1] ?? '')) return { ask: 'Sure. What day and time should it be?' };
    // "Make it a surprise" isn't a move.
    if (made && !moved && /^(?:a|an|so|more|less|bigger|smaller|quick|short|fancy|casual|special|happen|work)\b/.test(target)) return undefined;
    const place = target.replace(/^(?:at |make it |do |let's do |how about )/, '').trim();
    return place ? { place, plan } : undefined;
  }

  /** "Add June to the dinner", "invite Priya too", "also invite the family", "can Luis come too?" */
  /** "Add June to the book club dinner": who to add, and the plan when one is named. */
  #addOf(t: string): { who: string; plan?: string } | undefined {
    const lead = '^(?:(?:can|could|would) you |please |also |and |oh )*';
    const m =
      t.match(new RegExp(`${lead}(?:add|include)\\s+(.+)$`)) ??
      t.match(new RegExp(`${lead}(?:also )?invite\\s+(.+?)\\s+(?:too|as well)$`)) ??
      t.match(new RegExp(`${lead}also invite\\s+(.+)$`)) ??
      t.match(/^(?:can|could) (.+?) come too$/) ??
      t.match(new RegExp(`${lead}invite\\s+(.+?)\\s+to (?:it|the (?:plan|dinner|party|drinks|brunch|lunch|surprise|celebration|get together|thing|event))$`));
    const raw = m?.[1];
    if (!raw) return undefined;
    const tail = /(?:[\s,]+(?:too|as well|also|please))+$/;
    const target = raw.match(/\s+(?:to|on|in|into|for)\s+((?:the|my|our)\s+.+|[a-z]+'s\s+.+|it)$/);
    const who = (target ? raw.slice(0, target.index) : raw).replace(tail, '').trim();
    if (!who || /^(?:a|an|some|another|more|note|message|reminder)\b/.test(who)) return undefined;
    const ref = target?.[1]?.replace(/^(?:the|my|our)\s+/, '').replace(tail, '').trim();
    // "to the list" or "to the dinner" is the plan at hand; "to the book club dinner" names one.
    const plan = ref && !/^(?:it|plan|list|guest list|invites?|invite list|group|thing|event|dinner|party|drinks|brunch|lunch|surprise|celebration|get together)$/.test(ref) ? ref : undefined;
    return { who, plan };
  }

  /** One of the places just read out, when the words name it on their own: "Rosa's Cantina", "O'Cross's Cantina". */
  #placeNamed(text: string): string | undefined {
    if (this.#next?.action !== 'book' || !this.#places.length || text.split(' ').length > 5) return undefined;
    return matchClosedSet(text, this.#places.map((name) => ({ id: name, names: [name] })))?.id;
  }

  #intentOf(text: string): string | undefined {
    if (HELP.test(text)) return 'help';
    if (THANKS.test(text)) return 'thanks';
    if (REPEAT.test(text)) return 'repeat';
    if (/^(?:please )?(?:go ahead and )?send (?:out )?(?:the |my |our )?invite(?:s|ations)?\b|^(?:invite|send (?:it|them) to) (?:everyone|everybody|them|all of them)$/.test(text)) return 'invite';
    if (/^forget\b/.test(text)) return 'forget';
    if (/^what (?:do|did) you (?:know|remember|have) (?:about|on)\b|^what(?:'s| is) (?:our|the|my) usual\b|^(?:do you )?remember anything about\b/.test(text)) return 'recall';
    if (/^(tell|let|message|text) (everyone|everybody|the group|all the guests|the guests|them|the gang)\b|^remind (?:everyone|everybody|the group|all the guests|the guests|them) (?:to|that)\b/.test(text)) return 'message';
    if (/^remember\b/.test(text)) return 'remember';
    if (this.#addOf(text)) return 'add';
    if ((/\bcancel\b/.test(text) && !/\bcancel that\b/.test(text)) || /\bcall (?:it|the \w+|everything) off\b/.test(text)) return 'cancel';
    // Rally can add guests but not take them off; say so rather than guess.
    if (/^(?:please )?(?:remove|uninvite|disinvite)\b|^take \w+ off\b|^drop \w+ from\b/.test(text)) return 'remove';
    if (this.#changeOf(text)) return 'change';
    if (/^(?:can we |could we |let's |lets )?(?:do|make it|how about|go with) (?:it )?(?:on )?(?:the )?(?:sun|mon|tues|wednes|thurs|fri|satur)day\b/.test(text)) return 'change';
    // "The second one" right after Rally read out places.
    if (this.#next?.action === 'book' && PICK.test(text)) return 'pick';
    // Any "book <place>" except the book club; the server resolves the place name.
    // Speech recognition sometimes runs "book" into the name ("booklark and lantern").
    // "Book a dinner for the book club" is a new plan, not a place.
    if (/\bbook (?:a |an |some )?(?:dinner|party|brunch|lunch|drinks|night out|get together|game night|barbecue|bbq|picnic) (?:for|with)\b/.test(text)) return 'start';
    if (/^(book|go with|let's go with|lets go with|pick|choose)\b(?! club)|\bbook (?!club\b)\w|^book(?!s\b|ed\b|ing\b|club)[a-z]{3,}/.test(text)) return 'book';
    if (/\b(places?|restaurants?|venues?|spots?|where (should|could|can) we)\b/.test(text)) return 'places';
    // "Something Mexican?" right after the places, or on its own.
    if (PREFERENCE.test(text) && text.split(' ').length <= 5 && (this.#next?.action === 'book' || /^(?:something|somewhere|anything|any|how about|what about|maybe|or|a|an|find|suggest|recommend|look for|search for)\b|^\w+$/.test(text))) return 'places';
    // "Remind me what's planned" is the question after it.
    if (/^remind me (?=(?:what|when|who|where|how)\b)/.test(text)) return this.#intentOf(text.replace(/^remind me /, ''));
    if (/^remind me\b/.test(text)) return 'reminder';
    if (/\b(nudge|remind|ping|chase)\b|\bsend (?:a |another )?reminder\b/.test(text)) return 'nudge';
    if (/\bwhat(?:'s| is) (?:going on|happening|new) with\b/.test(text)) return 'status';
    if (
      /\bwhat(?:'s| is) new\b|\bwhat did i miss\b|\bcatch me up\b|\banything (?:new|i need to (?:do|know))\b|\bwhat(?:'s| is) the plan\b|\bwhat am i doing\b|\bis (?:anything|something|there anything) (?:planned|happening|going on)\b|\bany plans\b|\bwhat(?:'s| is) (?:planned|on|happening|coming up|going on)\b|\bwhat else is (?:planned|on|happening|coming up)\b|\bwhat (?:do|have) (?:i|we) (?:got |have )?planned\b|\bdo (?:i|we) have (?:any )?(?:plans|anything)\b|\bam i (?:free|busy)\b/.test(text) ||
      // "Is Lin planning something for me?", "what's David up to?": the server decides what may be said.
      /\b(?:is|are) (?:\w+ )?(?:planning|plotting|hiding|organi[sz]ing|throwing) (?:something|anything|a (?:party|surprise|dinner)|me a)\b|\bwhat(?:'s| is| are) (?:\w+ )?up to\b|^what(?:'s| is) up$|\bis there (?:a|any) (?:surprise|party|dinner|celebration|plans?)\b|^(?:any )?plans (?:for|this|next|tonight|tomorrow)\b|\bwhat do i (?:need|have) to do\b|^anything(?: (?:planned|happening|going on|on))?(?: (?:this|next|tonight|tomorrow|on|for)\b.*)?$|\bwhat(?:'s| is) on (?:my|our) (?:calendar|schedule|agenda)\b/.test(text)
    )
      return 'whats';
    if (
      /\b(?:tell me about|what about|what's up with) (?:the |my |our )?(?:surprise|party|plan|secret|dinner)\b|\bhow(?:'s| is| are)\b.*\b(going|coming|looking|shaping)\b|\bstatus\b|^(?:so )?(?:what|how) was\b/.test(text) ||
      // "Who's coming?", "who has answered so far?", "has anyone replied?", "how many are in?"
      /\bwho(?:'s| is| has| have)? (?:coming|in|out|going|answered|replied|responded|rsvp'?d|said (?:yes|no)|confirmed|can(?:'t| not)? (?:make it|come))\b|\bwho (?:hasn't|has not|haven't|didn't) (?:answered|replied|responded)\b|\b(?:has|have) (?:anyone|everyone|everybody|people|they) (?:answered|replied|responded|rsvp'?d|said)\b|\bany (?:answers|replies|updates|news|word)\b|\bhow many (?:people )?(?:are |have )?(?:coming|in|said yes|answered|replied)\b|\bwhere (?:are|do) (?:we|things) stand\b|\b(?:give me an|any) update\b/.test(text) ||
      // "Is everyone coming?", "did Sam answer?", "is it still on?", "when's the party?"
      /\bwho(?:'s| is)? not (?:coming|going)\b|\b(?:is|are) (?:everyone|everybody|people|they|all) (?:coming|in|going)\b|\b(?:did|has|have) \w+ (?:answer|answered|reply|replied|respond|responded|rsvp'?d?)\b|\b(?:is|are) (?:it|we|the \w+) still on\b|\bdid (?:everyone|everybody) get\b|^when(?:'s| is) (?:the |my |our )?(?:party|dinner|surprise|plan|drinks|brunch|book club|get together)\b|^where are we (?:going|eating|meeting)\b|^what time is (?:the )?(?:party|dinner|drinks|brunch)\b/.test(text)
    )
      return 'status';
    if (
      /\b(plan|rally|organi[sz]e|throw|set up|arrange|put together|round up|gather)\b|^(?:please )?(?:invite|host|schedule)\b|\bget .+ together\b|\bget together\b|\bhave .+ (?:over|round)\b/.test(text) ||
      /\b(?:let's|lets|we should|i want to|i'd like to|i would like to|can we|could we) (?:have|do|grab|get|go (?:for|out for)) (?:a |an |some )?(?:\w+ )?(?:dinner|drinks|brunch|lunch|party|bbq|barbecue|picnic|get together|game night|night out)\b|^(?:a |an )?(?:\w+ )?(?:dinner|drinks|brunch|lunch|party|bbq|barbecue|picnic|get together|game night)\b.*\b(?:with|for) (?!dinner|drinks|brunch|lunch)\w/.test(text)
    )
      return 'start';
    // A place from the list just read out, named on its own, even misheard.
    if (this.#placeNamed(text)) return 'book';
    return undefined;
  }

  async #start(text: string, listText: string, call: Call, speechOf: SpeechOf): Promise<string> {
    // "Plan book club like last time": reuse the routine Rally learned for that circle.
    const USUAL = /\s*,?\s*\b(?:like last time|same as last time|the usual|as usual|like we usually do)\b/;
    if (USUAL.test(text)) {
      const who = text.replace(USUAL, '').replace(/^(?:plan|rally|organi[sz]e|set up|arrange|do)\s+(?:the |our |my )?/, '').replace(/\s+(?:again|for (?:dinner|drinks|brunch))$/, '').trim();
      if (who) return speechOf(await call('start_plan', { invite: [who], like_last_time: true }));
    }
    const surprise = /\b(surprise|secret)\b/.test(text);
    const whenMatch = text.match(DATE_PHRASE);
    const when = whenMatch ? cleanWhen(whenMatch[0]) : undefined;
    let rest = whenMatch ? text.replace(whenMatch[0], ' ') : text;
    const ordinalWord = Object.keys(ORDINAL_WORDS).find((w) => rest.includes(w.replace('_', ' ')));
    const ordinal = rest.match(/\b(\d{1,3})(st|nd|rd|th)\b/)?.[0] ?? (ordinalWord ? ORDINAL_WORDS[ordinalWord] : undefined);
    const kindHit = KINDS.find(([re]) => re.test(rest));
    const kind = kindHit?.[1];
    const celebrating = surprise || Boolean(ordinal) || /\b(party|birthday|celebrat\w*|anniversary|retirement|farewell|graduation)\b/.test(rest);

    // Who: "invite X", "with X", "rally X", "get X together", "have X over", or "for X" when X is a group or names.
    const listRest = listText.replace(DATE_PHRASE, ' ').replace(/\s+/g, ' ');
    const inviteList = phraseAfter(listRest, /\binvite /g)[0] ?? phraseAfter(listRest, /\bwith /g)[0];
    const gatherList =
      listRest.match(/\bget (.+?) together\b/)?.[1] ??
      listRest.match(/\bhave (.+?) (?:over|round)\b/)?.[1] ??
      listRest.match(/\bbring (.+?) together\b/)?.[1] ??
      phraseAfter(listRest, /\b(?:rally|gather|round up) /g)[0];
    let invite = [inviteList, gatherList].filter((x): x is string => Boolean(x) && !NOT_GUESTS.test(x!)).map(splitNames).find((names) => names.length) ?? [];

    // The guest of honor: "for David's 60th", "David's surprise party", "a party for David", "a surprise for my husband".
    const possessive = (rest.match(/\bfor ([a-z]+)'s\b/) ?? rest.match(/(?:^|\s)([a-z]+)'s (?:surprise |birthday |retirement |\d+(?:st|nd|rd|th) )*(?:party|dinner|birthday|celebration|\d+(?:st|nd|rd|th))\b/))?.[1];
    const forWho = rest.match(new RegExp(`\\b(?:party|dinner|celebration|birthday|surprise|drinks|brunch|lunch)(?: \\w+)? for (my ${RELATION}|[a-z]+)\\b`))?.[1];
    let honoree = possessive && !['let', 'it', 'that', 'what', 'there', 'who', 'here', 'he', 'she'].includes(possessive) ? possessive : undefined;
    if (!honoree && forWho && !NOT_GUESTS.test(forWho) && !GROUP.test(forWho) && !/^(?:the|our|his|her|their)$/.test(forWho)) {
      // "Dinner for Priya" with nobody else named is a dinner with Priya; a party, a surprise, or a
      // dinner "with the family" is in their honor.
      if (forWho.startsWith('my ') || celebrating || invite.length) honoree = forWho;
    }
    if (!invite.length) {
      const forGuests = phraseAfter(listRest, /\bfor /g).find((p) => !NOT_GUESTS.test(p) && !/'s\b/.test(p) && !new RegExp(`^my ${RELATION}$`).test(p) && (GROUP.test(p) || (!celebrating && p !== honoree)));
      if (forGuests) invite = splitNames(forGuests);
    }
    rest = rest.replace(/\s+/g, ' ').trim();

    const honoreeName = honoree && !honoree.startsWith('my ') ? cap(honoree) : undefined;
    const kindWord = kindHit?.[2] ?? kindWordOf(rest) ?? kind;
    const occasion = rest.match(/\b(birthday|retirement|anniversary|graduation|farewell)\b/)?.[1];
    const title = honoreeName ? `${honoreeName}'s ${surprise ? 'surprise ' : ''}${ordinal ?? `${occasion ? `${occasion} ` : ''}${kind === 'dinner' ? 'dinner' : 'party'}`}` : honoree ? undefined : groupTitle(invite, kindWord);
    // Asked "who should I invite?" and answered with a whole request ("rally the family"): keep what was said before.
    const earlier = this.#frame?.kind === 'start' ? this.#frame : undefined;
    if (earlier) this.#frame = undefined;
    const args: Record<string, unknown> = { ...earlier?.args, ...(title ? { title } : {}), ...(when ? { when } : {}), ...(kind ? { kind } : {}), ...(honoree ? { honoree } : {}), ...(surprise ? { surprise: true } : {}) };
    if (earlier?.args.honoree && !honoree) {
      if (earlier.args.title) args.title = earlier.args.title;
      else delete args.title;
    }
    if (!invite.length) {
      this.#frame = { kind: 'start', args, kindWord: kindWord ?? earlier?.kindWord };
      // Don't repeat who a surprise is for: the Echo may not know who else is in the room.
      return 'Who should I invite? You can name circles, like the family, or people.';
    }
    return speechOf(await call('start_plan', { ...args, invite }));
  }
}

function splitNames(text: string): string[] {
  return text
    .replace(/\b(please|too|as well|also)\b/g, '')
    .split(/\s*(?:,|\band\b|&|\bplus\b)\s*/)
    .map((s) => s.trim())
    .filter((s) => s.length > 1);
}

/** Each phrase that follows a keyword, up to the next preposition: "for the family on Sunday" → "the family". */
function phraseAfter(text: string, keyword: RegExp): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(keyword)) {
    const tail = text.slice(m.index! + m[0].length);
    const end = tail.search(/\s(?:for|to|on|at|with|this|next|in|so|because|who|which|and then)\b|[?.!;]/);
    const phrase = (end < 0 ? tail : tail.slice(0, end)).replace(/[\s,]+$/, '').trim();
    if (phrase) out.push(phrase);
  }
  return out;
}

/** "The book club" and "his golf buddies" as a title: "Book club dinner", "Dinner with the golf buddies", "Drinks with Priya and June". */
function groupTitle(invite: string[], kindWord = 'dinner'): string | undefined {
  if (!invite.length) return undefined;
  const bare = (g: string) => g.replace(/^(?:the|my|our|his|her|their) /, '');
  const first = bare(invite[0]!);
  if (invite.length === 1 && GROUP.test(first) && !/s$/.test(first)) return `${cap(first)} ${kindWord}`;
  // Guests read the title too, so "my mom" becomes "Mom".
  const named = invite.map((g) => (GROUP.test(g) ? `the ${bare(g)}` : new RegExp(`^(?:my|our|his|her|their) ${RELATION}$`).test(g) ? cap(bare(g)) : /^the /.test(g) ? g : g.replace(/\b[a-z]/g, (c) => c.toUpperCase())));
  const list = named.length > 3 ? [...named.slice(0, 2), 'friends'] : named;
  const people = list.length > 1 ? `${list.slice(0, -1).join(', ')} and ${list.at(-1)}` : list[0]!;
  return `${cap(kindWord)} with ${people}`.slice(0, 80);
}

function kindWordOf(text: string): string | undefined {
  const hit = KINDS.find(([re]) => re.test(text));
  if (!hit) return /\bget together\b/.test(text) ? 'get-together' : undefined;
  return hit[2] ?? text.match(hit[0])?.[1] ?? hit[1];
}

/** A date as said, tidied for the server: "on sunday" → "sunday", "at 6 30 p m" → "at 6:30 pm". */
function cleanWhen(w: string): string {
  return w
    .replace(/^on /, '')
    .replace(/,? at (\d{1,2}) (\d{2})\b/, ' at $1:$2')
    .replace(/\b([ap]) m\b/, '$1m')
    .trim();
}

/** A time on its own: "8", "8 pm", "at eight thirty", "7:30" → "8 pm", "8:30 pm", "7:30 pm". */
function timeOf(target: string): string | undefined {
  const t = target
    .replace(/\b(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\b(?: (thirty|fifteen|forty five|forty-five))?/, (_, h: string, m?: string) => `${NUMBER_WORDS[h]}${m ? `:${m === 'thirty' ? '30' : m === 'fifteen' ? '15' : '45'}` : ''}`)
    .replace(/\b([ap])\.? ?m\.?\b/, '$1m');
  const m = t.match(/^(?:at |around |about |for )?(\d{1,2})(?:[: ](\d{2}))?\s*(am|pm|o'?clock)?(?: (?:tonight|in the evening|at night))?$/);
  if (!m) return undefined;
  const hour = Number(m[1]);
  if (hour < 1 || hour > 12 || (m[2] && Number(m[2]) > 59)) return undefined;
  // Like "at 7" for a social plan: evening, except late morning.
  const period = m[3] === 'am' || m[3] === 'pm' ? m[3] : hour === 11 ? 'am' : 'pm';
  return `${hour}${m[2] ? `:${m[2]}` : ''} ${period}`;
}

/** "Tom can't do Mondays" → Tom / "can't do mondays"; "the book club likes Mexican" → book club / "likes mexican". */
function subjectAndRest(body: string, firstWord = true): { about: string; rest: string } | undefined {
  const b = body.replace(/^that /, '').replace(/^(?:the|our|my) /, '').trim();
  const verb = b.search(/\s(?:can't|cannot|can|doesn't|does|don't|do|is|isn't|are|aren't|was|has|hasn't|have|likes?|loves?|hates?|prefers?|uses?|wants?|needs?|won't|will|always|never|usually|only|works?|goes|meets|plays|gets|must|should|eats|drinks|lives)\b/);
  if (verb > 0) return { about: b.slice(0, verb).replace(/'s$/, '').trim(), rest: b.slice(verb).trim() };
  const possessive = b.match(/^([a-z]+)'s\s+(.+)$/);
  if (possessive) return { about: possessive[1]!, rest: possessive[2]! };
  const m = firstWord ? b.match(/^([a-z]+)\s+(.+)$/) : undefined;
  return m ? { about: m[1]!, rest: m[2]! } : undefined;
}
